// The installable app's service worker (public/sw.js): registered in production builds only (the
// dev server reloads on its own). A new release installs a new worker in the background; the app
// then offers "New version · Reload" rather than switching under the user mid-trade. An installed
// app can stay open for days, so it also checks for a release every 30 minutes.
import { toast } from 'sonner';

// Android Chrome's install prompt fires once, early: kept here for the install tip to use.
export interface InstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<unknown> }
export let installPrompt: InstallPromptEvent | null = null;
const listeners = new Set<(e: InstallPromptEvent | null) => void>();
export function onInstallPrompt(f: (e: InstallPromptEvent | null) => void) { listeners.add(f); return () => { listeners.delete(f); }; }
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // our tip asks instead of Chrome's mini bar
    installPrompt = e as InstallPromptEvent;
    for (const f of listeners) f(installPrompt);
  });
  window.addEventListener('appinstalled', () => { installPrompt = null; for (const f of listeners) f(null); });
}

export function registerServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    location.reload();
  });
  const offer = (w: ServiceWorker) => toast('A new version of MoneyHQ is ready', {
    duration: Infinity,
    action: { label: 'Reload', onClick: () => w.postMessage('skip-waiting') },
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          // A worker waiting while another controls the page is an update (the first install isn't).
          if (w.state === 'installed' && navigator.serviceWorker.controller) offer(w);
        });
      });
      setInterval(() => { void reg.update().catch(() => {}); }, 30 * 60_000);
    }).catch(() => { /* the app works without it */ });
  });
}
